import {ApiResponse} from "./types";

export class ApiClient {

    private baseUrl: string;

    constructor(baseUrl: string) {
        this.baseUrl = baseUrl;
    }

    /**
     * Generic request method that handles all HTTP calls
     */
    async request<T>(
        endpoint?: string,
        options?: RequestInit
    ): Promise<ApiResponse<T>> {
        try {
            let url = '';
            if (endpoint) {
                url = `${this.baseUrl}${endpoint}`;
            } else {
                url = `${this.baseUrl}`;
            }

            const response = await fetch(url, {
                ...options,
                credentials: 'include', // Important for cookies
                headers: {
                    'Content-Type': 'application/json',
                    ...options?.headers,
                },
            });

            // Handle empty responses (like 204 No Content)
            if (response.status === 204 || response.headers.get('content-length') === '0') {
                return {
                    success: response.ok,
                    data: undefined,
                    error: response.ok ? undefined : {
                        code: 'UNEXPECTED_ERROR',
                        message: 'Empty error response'
                    }
                };
            }

            const data: ApiResponse<T> = await response.json();
            return data;

        } catch (error) {
            console.error('API request failed:', error);
            return {
                success: false,
                error: {
                    code: 'NETWORK_ERROR',
                    message: 'Netzwerkfehler - Bitte überprüfen Sie Ihre Internetverbindung'
                }
            };
        }
    }

    /**
     * POST request with FormData (for file uploads)
     *
     * Usage:
     * const formData = new FormData();
     * formData.append('file', file);
     * formData.append('name', 'My Template');
     * formData.append('description', 'Template description');
     *
     * const response = await apiClient.postFormData('/templates', formData);
     */
    async postFormData<T>(
        endpoint: string,
        formData: FormData,
        onProgress?: (progress: number) => void
    ): Promise<ApiResponse<T>> {
        try {
            const response = await fetch(`${this.baseUrl}${endpoint}`, {
                method: 'POST',
                credentials: 'include',
                // IMPORTANT: Don't set Content-Type header!
                // Browser will automatically set it to 'multipart/form-data' with boundary
                headers: {
                    // Don't include 'Content-Type': 'application/json'
                    // Let the browser handle it
                },
                body: formData,
            });

            // Handle empty responses (like 204 No Content)
            if (response.status === 204 || response.headers.get('content-length') === '0') {
                return {
                    success: response.ok,
                    data: undefined,
                    error: response.ok ? undefined : {
                        code: 'UNEXPECTED_ERROR',
                        message: 'Empty error response'
                    }
                };
            }

            const data: ApiResponse<T> = await response.json();
            return data;

        } catch (error) {
            console.error('Form data upload failed:', error);
            return {
                success: false,
                error: {
                    code: 'NETWORK_ERROR',
                    message: 'Netzwerkfehler - Bitte überprüfen Sie Ihre Internetverbindung'
                }
            };
        }
    }

    /**
     * Download file (returns Blob instead of JSON)
     */
    async downloadFile(
        endpoint: string,
        body?: any
    ): Promise<{ success: boolean; blob?: Blob; filename?: string; error?: { code: string; message: string } }> {
        try {
            const response = await fetch(`${this.baseUrl}${endpoint}`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: body ? JSON.stringify(body) : undefined,
            });

            if (!response.ok) {
                // Try to parse error as JSON
                try {
                    const errorData = await response.json();
                    return {
                        success: false,
                        error: {
                            code: 'DOWNLOAD_ERROR',
                            message: errorData.message || 'Download fehlgeschlagen'
                        }
                    };
                } catch {
                    return {
                        success: false,
                        error: {
                            code: 'DOWNLOAD_ERROR',
                            message: `Download fehlgeschlagen (Status: ${response.status})`
                        }
                    };
                }
            }

            // Extract filename from Content-Disposition header
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = 'download';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            }

            const blob = await response.blob();

            return {
                success: true,
                blob,
                filename
            };

        } catch (error) {
            console.error('File download failed:', error);
            return {
                success: false,
                error: {
                    code: 'NETWORK_ERROR',
                    message: 'Netzwerkfehler - Bitte überprüfen Sie Ihre Internetverbindung'
                }
            };
        }
    }

    /**
     * POST request
     */
    async post<T>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, {
            method: 'POST',
            body: body ? JSON.stringify(body) : undefined,
        });
    }

    /**
     * GET request
     */
    async get<T>(endpoint?: string): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, {
            method: 'GET'
        });
    }

    /**
     * PUT request
     */
    async put<T>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, {
            method: 'PUT',
            body: body ? JSON.stringify(body) : undefined,
        });
    }

    /**
     * PATCH request
     */
    async patch<T>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, {
            method: 'PATCH',
            body: body ? JSON.stringify(body) : undefined,
        });
    }

    /**
     * DELETE request
     */
    async delete<T>(endpoint: string, body?: any): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, {
            method: 'DELETE',
            body: body ? JSON.stringify(body) : undefined,
        });
    }
}